import React, {useCallback, useEffect} from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faInfoCircle } from '@fortawesome/free-solid-svg-icons';
import { FLOOR_TYPES } from '../../constants/floorTypes';
import {FloorForm} from "./FloorForm";

// Legacy FloorConfiguration recreation (auto-expanding floors, tooltip, filtered floor type availability)
const FloorConfiguration = ({ selectedFloors = [], initialFlors = [], onFloorChange, totalFloors }) => {
  // Ensure at least one initial floor
  useEffect(() => {
    if (selectedFloors.length === 0) {
      const initialFloor = {
        _id: `floor_${Date.now()}`,
        floorType: FLOOR_TYPES.UNKNOWN.value,
        floorNumber: 0,
        display_name: FLOOR_TYPES.UNKNOWN.label,
        units: {
          commercialUnitCount: 0,
          livingUnitCount: 0
        },
      };
      onFloorChange([initialFloor]);
    }
  }, [selectedFloors.length, onFloorChange]);

  const addFloorIfNeeded = () => {
    const newFloor = {
      _id: `floor_${Date.now()}`,
      floorType: FLOOR_TYPES.UNKNOWN.value,
      floorNumber: selectedFloors.length + 1,
      display_name: FLOOR_TYPES.UNKNOWN.label,
      units: {
       livingUnitCount: 0,
       commercialUnitCount: 0
      }
    };
    onFloorChange([...selectedFloors, newFloor]);
  };

  const removeFloor = (index) => {
    if (selectedFloors.length <= 1) return; // Keep at least one floor
    const updated = selectedFloors
      .filter((_, idx) => idx !== index)
      .map((f, idx) => ({ ...f, floor_number: idx }));
    onFloorChange([...updated]);
  };

  const onFloorChanged = useCallback((updatedFloor, index) => {
    const updated = [...selectedFloors];
    updated[index] = updatedFloor;
    onFloorChange(updated);
  }, [selectedFloors, onFloorChange]);

  return (
    <div className="space-y-4">
      <div className="flex items-center space-x-2">
        <h3 className="text-lg font-semibold">Etagen-Konfiguration</h3>
        <div className="relative group">
          <FontAwesomeIcon icon={faInfoCircle} className="text-blue-500 hover:text-blue-600 cursor-help text-sm" title="Etagen-Konfiguration Info" />
          <div className="building-logic-tooltip absolute top-6 w-64 p-3 bg-yellow-50 border border-yellow-200 rounded-lg shadow-lg z-10 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200">
            <h4 className="text-sm font-medium text-yellow-800 mb-2">Etagen-Konfiguration Info:</h4>
            <div className="text-xs text-green-700 mb-3 p-2 bg-green-50 rounded border border-green-200">
              <div className="flex items-center space-x-1 mb-1">
                <svg className="w-3 h-3 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <strong>Hinzufügen:</strong>
              </div>
              <p>Verwenden Sie den "+ Etage hinzufügen" Button um weitere Etagen hinzuzufügen.</p>
            </div>
            {selectedFloors.some(f => f.floorType !== FLOOR_TYPES.UNKNOWN.value) && (
              <>
                <h5 className="text-xs font-medium text-yellow-800 mb-1">Aktive Gebäude-Logik:</h5>
                <div className="text-xs text-yellow-700 space-y-1">
                  {selectedFloors.some(f => f.floorType === FLOOR_TYPES.ATTIC_FLOOR.value) && (<p>• Dachgeschoss erreicht - keine weiteren Etagen möglich</p>)}
                  {selectedFloors.some(f => [FLOOR_TYPES.GROUND_FLOOR.value, FLOOR_TYPES.HIGH_NORMAL_FLOOR.value, FLOOR_TYPES.NORMAL_FLOOR.value].includes(f.floorType)) && (<p>• Höhere Etagen gewählt - bestimmte Etagen-Typen nicht mehr verfügbar</p>)}
                  {selectedFloors.some(f => f.floorType === FLOOR_TYPES.BASEMENT_FLOOR.value) && (<p>• Keller gewählt - alle Etagen-Typen weiterhin verfügbar</p>)}
                  {selectedFloors.length > 1 && !selectedFloors.some(f => [FLOOR_TYPES.BASEMENT_FLOOR.value, FLOOR_TYPES.GROUND_FLOOR.value, FLOOR_TYPES.HIGH_NORMAL_FLOOR.value, FLOOR_TYPES.NORMAL_FLOOR.value, FLOOR_TYPES.ATTIC_FLOOR.value].includes(f.floorType)) && (<p>• Deutsche Bauordnung wird automatisch angewendet</p>)}
                </div>
              </>
            )}
          </div>
        </div>
      </div>

      <p className="text-sm text-gray-600">Definieren Sie die Struktur des Gebäudes von unten nach oben. Verwenden Sie den "+ Etage hinzufügen" Button um weitere Etagen hinzuzufügen:</p>

      <div className="space-y-3">
        {selectedFloors?.map((floor, index) => {
          return (<FloorForm
              key={floor.externalId || floor._id || `floor${index}`}
              floors={selectedFloors}
              initialFloor={initialFlors.find(initFl => initFl.externalId === floor?.externalId)}
              currentFloor={floor}
              index={index}
              onChange={onFloorChanged}
              onAddFloor={addFloorIfNeeded}
              onRemoveFloor={removeFloor}
          />);
        })}
      </div>

      <div className="bg-blue-50 p-3 rounded-lg">
        <h4 className="text-sm font-medium mb-2" style={{ color: '#707A86' }}>Gebäudestruktur (von unten nach oben):</h4>
        <div className="text-sm space-y-1" style={{ color: '#707A86' }}>
          {selectedFloors.map((floor, index) => {
            const floorSummary = [];
            if (floor?.units?.livingUnitCount > 0) floorSummary.push(`${floor?.units?.livingUnitCount} WE`);
            if (floor?.units?.commercialUnitCount > 0) floorSummary.push(`${floor?.units?.commercialUnitCount} GE`);
            return (
              <div key={`floor${index}_summary`} className="flex justify-between">
                <span>Etage {index + 1}:</span>
                <span className="font-medium">
                  {FLOOR_TYPES[floor.floorType || FLOOR_TYPES.UNKNOWN.value]?.label}
                  {floorSummary.length > 0 && (
                    <span className="ml-2 text-xs text-blue-600">({floorSummary.join(', ')})</span>
                  )}
                </span>
              </div>
            );
          })}
        </div>
        <div className="mt-3 pt-2 border-t" style={{ borderColor: '#707A86' }}>
          <div className="text-sm space-y-1" style={{ color: '#707A86' }}>
            <div className="flex justify-between"><strong>Gesamt Etagen:</strong><span>{selectedFloors.length}</span></div>
            <div className="flex justify-between"><strong>Gesamt Wohneinheiten:</strong><span>{selectedFloors.reduce((s,f)=> s + (f?.units?.livingUnitCount || 0),0)}</span></div>
            <div className="flex justify-between"><strong>Gesamt Gewerbeeinheiten:</strong><span>{selectedFloors.reduce((s,f)=> s + (f?.units?.commercialUnitCount || 0),0)}</span></div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FloorConfiguration;
